import { useEffect, useMemo, useRef } from 'react';

import { useDispatch } from 'react-redux';

import { trackProductListSwatchClicked } from '~/shared/actions';
import getCartPrice from '~/shared/utils/getCartPrice';
import useSecondarySite from '~/shared/utils/useSecondarySite';
import { useMembership } from '~/techstyle-shared/react-accounts';
import { useCart } from '~/techstyle-shared/react-cart';
import {
  trackProductClicked,
  useProductListViewed,
} from '~/techstyle-shared/react-products';

import useUnifiedProductGridData from './useUnifiedProductGridData';

const useProductCardTracking = ({
  activeProduct = {},
  productSource,
  gridIndex,
}) => {
  const productCardRef = useRef();
  const { observe, unobserve } = useProductListViewed();
  const dispatch = useDispatch();
  const { isVipCart } = useCart({ isLoginRequired: true }) || {};
  const { isVip } = useMembership();
  const secondarySite = useSecondarySite();
  const { isRelatedProductsFetching } = useUnifiedProductGridData();

  const {
    masterProductId,
    label,
    pricing = {},
    reviewCount,
    pdpLink,
  } = activeProduct;

  const product = useMemo(
    () => ({
      ...activeProduct,
      defaultUnitPrice: pricing?.vip,
      retailUnitPrice: pricing?.retail,
      saleUnitPrice: pricing?.sales || pricing?.vip,
      promos: {
        promoPrice: pricing?.finalDetails?.promoPrice,
        promoLabel: pricing?.finalDetails?.promoLabel,
      },
    }),
    [activeProduct, pricing]
  );

  const constructorItemPrice = getCartPrice({
    product,
    isVipCart,
    isVip,
    secondarySite,
  });

  const trackingData = useMemo(
    () => ({
      gridIndex,
      productSource,
      href: pdpLink,
      product,
    }),
    [gridIndex, productSource, pdpLink, product]
  );

  useEffect(() => {
    const node = productCardRef.current;
    const hasPricing = trackingData.product.pricing;

    if (node && hasPricing && !isRelatedProductsFetching) {
      observe(node, trackingData);
      return () => unobserve(node);
    }
  }, [observe, unobserve, trackingData, isRelatedProductsFetching]);

  const trackingTags = {
    'data-cnstrc-item-id': masterProductId,
    'data-cnstrc-item-name': label,
    'data-cnstrc-item-price': constructorItemPrice,
    'data-testid': 'product-cell',
    'data-autotag':
      reviewCount > 0
        ? 'prodgrid-prod-with-rating'
        : 'pdp-modal-styledSelect-list',
  };

  const trackProductClick = (event) => {
    dispatch(trackProductClicked(trackingData));
  };

  const trackProductSwatchClick = (trackingData) => {
    dispatch(trackProductListSwatchClicked(trackingData));
  };

  return {
    trackProductClick,
    productCardRef,
    trackingTags,
    trackProductSwatchClick,
  };
};

export default useProductCardTracking;
